import React, { useState, useEffect, useCallback } from "react";
import { Alert } from "reactstrap";
import BizimhesapSettingsTab from "./BizimhesapSettingsTab";
import ParasutSettingsTab from "./ParasutSettingsTab";
import { fetchBizimhesapSettings } from "../../../api/requests/bizimhesap-requests";
import { fetchParasutSettings } from "../../../api/requests/parasut-requests";

import { integrationsByCategory } from "./integrations/registry";
import IntegrationSearch from "./integrations/IntegrationSearch";
import IntegrationCard from "./integrations/IntegrationCard";
import IntegrationConfigModal from "./integrations/IntegrationConfigModal";

import { usePlanIntegrations } from "../../../hooks/usePlanIntegrations";

const ACCOUNTING = integrationsByCategory("accounting");
const otherOf = (id) => (id === "bizimhesap" ? "parasut" : "bizimhesap");
const labelOf = (id) => ACCOUNTING.find((p) => p.id === id)?.name;

const trIncludes = (text, term) =>
    (text || "").toLocaleLowerCase("tr-TR").includes((term || "").toLocaleLowerCase("tr-TR"));

/**
 * Muhasebe entegrasyonu — kart-grid hub. Her sağlayıcı bir kart; "Yapılandır/Bağla"
 * tıklanınca mevcut sağlayıcı formu (ParasutSettingsTab / BizimhesapSettingsTab) bir
 * modal içinde açılır. Aynı anda yalnızca BİR entegrasyon aktif olabilir (kural
 * backend'de zorlanır; kaydedince kardeş kart tazelenir).
 */
export default function AccountingSettingsTab({ companyId }) {
    const { has: hasIntegration } = usePlanIntegrations();
    // OAuth dönüşünde (?parasut=...) doğrudan Paraşüt modalını aç
    const cameFromParasut =
        typeof window !== "undefined" && !!new URLSearchParams(window.location.search).get("parasut");

    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(cameFromParasut ? "parasut" : null);
    // Kardeş formu, sibling kaydedince remount edip güncel duruma çekmek için
    const [reload, setReload] = useState({ bizimhesap: 0, parasut: 0 });
    const [status, setStatus] = useState({
        bizimhesap: { configured: false, enabled: false },
        parasut: { configured: false, enabled: false },
    });

    const loadStatus = useCallback(async () => {
        if (!companyId) return;
        const [bh, ps] = await Promise.all([
            fetchBizimhesapSettings(companyId).catch(() => null),
            fetchParasutSettings(companyId).catch(() => null),
        ]);
        setStatus({
            bizimhesap: { configured: !!bh?.data, enabled: !!bh?.data?.enabled },
            parasut: { configured: !!ps?.data, enabled: !!ps?.data?.enabled },
        });
    }, [companyId]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    // Bir entegrasyon kaydedildiğinde: rozetleri tazele + KARDEŞ formu remount et
    // (backend kardeşi otomatik pasifleştirdiği için güncel durumu yüklenmeli) + modalı kapat.
    const handleSaved = (savedId) => {
        loadStatus();
        const sibling = otherOf(savedId);
        setReload((r) => ({ ...r, [sibling]: r[sibling] + 1 }));
        setSelectedId(null);
    };

    const statusOf = (integration) => {
        // Paket kapsamı dışındaki entegrasyon kilitli çizilir; kaydetmeyi backend
        // zaten 403 ile engelliyor, burada form hiç doldurtulmuyor.
        if (!hasIntegration(integration.planIntegration)) return "locked";
        const st = status[integration.id];
        if (!st) return "passive";
        return st.enabled ? "connected" : "passive";
    };

    const cards = ACCOUNTING.filter((i) => trIncludes(i.name, search));

    const selected = selectedId ? ACCOUNTING.find((i) => i.id === selectedId) : null;
    const selectedConfigured = selectedId ? status[selectedId]?.configured : false;
    const otherActiveLabel =
        selectedId && status[otherOf(selectedId)].enabled ? labelOf(otherOf(selectedId)) : null;

    const closeModal = () => setSelectedId(null);

    return (
        <>
            <p className="text-muted mb-3">
                Cari hareketleriniz seçtiğiniz muhasebe programına otomatik aktarılır. Anlaşmalı
                olduğunuz programı seçip yapılandırın.
            </p>

            <Alert color="info" className="mb-4 shadow-sm" style={{ borderLeft: "4px solid #0dcaf0" }}>
                <i className="bx bx-info-circle me-1" />
                Aynı anda yalnızca <strong>bir</strong> muhasebe entegrasyonu aktif olabilir. Bir
                entegrasyonu aktif edip kaydettiğinizde, diğeri otomatik olarak pasifleştirilir.
            </Alert>

            <div className="d-flex flex-wrap align-items-center justify-content-end gap-3 mb-4">
                <IntegrationSearch value={search} onChange={setSearch} />
            </div>

            {cards.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <i className="bx bx-search-alt fs-1 d-block mb-2" />
                    Aramanızla eşleşen entegrasyon bulunamadı.
                </div>
            ) : (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                        gap: "1rem",
                    }}
                >
                    {cards.map((integration) => (
                        <IntegrationCard
                            key={integration.id}
                            integration={integration}
                            status={statusOf(integration)}
                            onAction={() => statusOf(integration) === "locked" ? null : setSelectedId(integration.id)}
                        />
                    ))}
                </div>
            )}

            {selected && (
                <IntegrationConfigModal
                    isOpen
                    toggle={closeModal}
                    integration={selected}
                    title={`${selected.name} ${selectedConfigured ? "Yapılandır" : "Bağla"}`}
                    subtitle="Entegrasyon ayarlarını düzenleyin"
                >
                    {selected.id === "parasut" ? (
                        <ParasutSettingsTab
                            key={`parasut-${reload.parasut}`}
                            companyId={companyId}
                            otherActiveLabel={otherActiveLabel}
                            onSaved={() => handleSaved("parasut")}
                        />
                    ) : (
                        <BizimhesapSettingsTab
                            key={`bizimhesap-${reload.bizimhesap}`}
                            companyId={companyId}
                            otherActiveLabel={otherActiveLabel}
                            onSaved={() => handleSaved("bizimhesap")}
                        />
                    )}
                </IntegrationConfigModal>
            )}
        </>
    );
}
